import { dot, headingVector, length, rightVector, scale, subtract } from '../game/math'
import { getRacerIdentity } from '../game/racer-config'
import type { SimulationState } from '../game/types'

export type ProximityDirection = 'FRONT' | 'RIGHT' | 'REAR' | 'LEFT'

export interface ProximityContact {
  vehicleId: string
  name: string
  distance: number
  relativeAngle: number
  direction: ProximityDirection
  closingSpeed: number
  inFireWindow: boolean
}

function directionFor(angle: number): ProximityDirection {
  if (Math.abs(angle) <= Math.PI / 4) return 'FRONT'
  if (Math.abs(angle) >= (Math.PI * 3) / 4) return 'REAR'
  return angle > 0 ? 'RIGHT' : 'LEFT'
}

export function buildProximityContacts(state: SimulationState, limit = 3): ProximityContact[] {
  const player = state.vehicles.find((vehicle) => vehicle.id === state.race.playerId)
  if (!player) return []
  const forward = headingVector(player.heading)
  const right = rightVector(player.heading)
  return state.vehicles
    .filter((vehicle) => vehicle.id !== player.id)
    .map((vehicle) => {
      const offset = subtract(vehicle.position, player.position)
      const distance = length(offset)
      const directionVector = distance > 1e-6 ? scale(offset, 1 / distance) : forward
      const relativeAngle = Math.atan2(dot(directionVector, right), dot(directionVector, forward))
      const relativeVelocity = subtract(vehicle.velocity, player.velocity)
      return {
        vehicleId: vehicle.id,
        name: vehicle.displayName ?? getRacerIdentity(vehicle.id).name,
        distance,
        relativeAngle,
        direction: directionFor(relativeAngle),
        closingSpeed: -dot(relativeVelocity, directionVector),
        inFireWindow: distance >= 3 && distance <= 42 && Math.abs(relativeAngle) <= 0.42,
      }
    })
    .sort((a, b) => a.distance - b.distance)
    .slice(0, Math.max(0, limit))
}
